<script>
import Swal from 'sweetalert2';
import { purchaseOrderToTicketData } from '@/serializers/PurchaseOrder.serializer';
import { mapGetters } from 'vuex';
import { FPUtils } from '@/utils/FloatingPointUtils';
import { PurchaseOrderProduct } from '@/models/PurchaseOrder/PurchaseOrderProduct';
import visa from '@/assets/images/icons/payments/img/visa-light.png';
import americanEx from '@/assets/images/icons/payments/img/american-express.png';
import masterCard from '@/assets/images/icons/payments/img/mastercard.png';
import SaleSuccessActions from '@/components/POS/SaleSuccessActions.vue';
import CurrencyFormatter from '../../models/CurrencyFormatter';
import InvoicesService from '../../services/InvoicesService';
import PurchaseOrderService from '../../services/PurchaseOrderService';
import ProductService from '../../services/ProductService';
import ClientService from '../../services/ClientService';
// import SatService from '../../services/SatService';

export default {
  name: 'new-payment',
  components: {
    SaleSuccessActions,
  },
  computed: {
    ...mapGetters({
      stores: 'stores',
      cartTotal: 'cartTotal',
      authUserData: 'authUserData',
      ticketStruct: 'ticketData',
      itemsList: 'itemsList',
      selectedClient: 'selectedClient',
      totalOrderPaid: 'totalOrderPaid',
      FTotalOrderPaid: 'FTotalOrderPaid',
      stompClient: 'stompClient',
      frontItemsList: 'frontItemsList',
      savedQuotationPurchaseOrderId: 'savedQuotationPurchaseOrderId',
      canViewPos: 'canViewPos',
      hasItemsWithNoInventory: 'hasItemsWithNoInventory',
      itemsWithNoInventory: 'itemsWithNoInventory',
      paymentInvoiceMisc: 'paymentInvoiceMisc',
      paymentInvoiceClients: 'paymentInvoiceClients',
      paymentFiscalRegimes: 'paymentFiscalRegimes',
      paymentUsoCfdiItems: 'paymentUsoCfdiItems',
      defaultFiscalRegimes: 'defaultFiscalRegimes',
      defaultUsoCfdiItems: 'defaultUsoCfdiItems',
    }),
    payButtonDisabled() {
      if (this.hasItemsWithNoInventory && this.selectedClient === null) {
        return true;
      }
      return false;
    },
    disableMakeTransactionButton() {
      const orderTotal = FPUtils.round2dec(Number(this.cartTotal.total || 0));
      const paidTotal = FPUtils.round2dec(Number(this.totalOrderPaid || 0));
      return paidTotal >= orderTotal || this.isProcessingPayment;
    },
    FRealPaidCash() {
      return CurrencyFormatter.format(this.realPaidCash);
    },
    remainingTotal() {
      const orderTotal = FPUtils.round2dec(Number(this.cartTotal.total || 0));
      const paidTotal = FPUtils.round2dec(Number(this.totalOrderPaid || 0));
      return FPUtils.round2dec(orderTotal - paidTotal);
    },
    FRemainingTotal() {
      if (this.remainingTotal > 0) {
        return CurrencyFormatter.format(this.remainingTotal);
      }
      return CurrencyFormatter.format(0);
    },
    cashChange() {
      const remaining = FPUtils.round2dec(this.remainingTotal);
      if (remaining >= 0) return 0;
      return FPUtils.round2dec(Math.abs(remaining));
    },
    FChange() {
      return CurrencyFormatter.format(this.cashChange);
    },
    disabledBtn() {
      return !(this.remainingTotal > 0);
    },
    lockSatPaymentFields() {
      return this.isSatCutoffCase();
    },
    requiresInvoice: {
      get() {
        return this.invoiceRequired === 'yes';
      },
    },
    fiscalRegimes() {
      if (Array.isArray(this.paymentFiscalRegimes)) {
        if (!this.invoiceClientSelected && !this.isValidRfc(this.clientRfc)) {
          return this.defaultFiscalRegimes.map((x) => ({
            label: `[${x.code}] ${x.name}`,
            value: String(x.code),
          }));
        }
        return this.paymentFiscalRegimes.map((x) => ({
          label: `[${x.code}] ${x.name}`,
          value: String(x.code),
        }));
      }
      return [];
    },
    usoCfdiItems() {
      if (Array.isArray(this.paymentUsoCfdiItems)) {
        if (!this.clientRegimeId) {
          return this.defaultUsoCfdiItems.map((x) => ({
            ...x,
            code: String(x.code ?? ''),
            name: String(x.name ?? ''),
            display: `${x.code} - ${x.name}`,
          }));
        }
        return this.paymentUsoCfdiItems.map((x) => ({
          ...x,
          code: String(x.code ?? ''),
          name: String(x.name ?? ''),
          display: `${x.code} - ${x.name}`,
        }));
      }
      return [];
    },
    visibleStepSequence() {
      const sequence = [1];
      if (this.hasItemsWithNoInventory) sequence.push(2);
      if (this.invoiceRequired === 'yes') sequence.push(3);
      return sequence;
    },
    renderedStep() {
      const idx = this.visibleStepSequence.indexOf(this.step);
      return idx >= 0 ? idx + 1 : 1;
    },
    renderedTotalSteps() {
      return this.visibleStepSequence.length;
    },
    isLastVisibleStep() {
      return this.step === this.visibleStepSequence[this.visibleStepSequence.length - 1];
    },
    enableFinishOrder() {
      if (this.remainingTotal > 0) return false;
      const noInventoryValidation = this.validateNoInventoryForm();
      if (this.hasItemsWithNoInventory && this.step >= 2 && noInventoryValidation !== true) {
        return false;
      }
      if (!this.isLastVisibleStep) return true;
      if (this.invoiceRequired === 'yes') {
        const invoiceValidation = this.validateInvoiceForm();
        return invoiceValidation === true;
      }
      return true;
    },
    finishOrderBtnText() {
      if (!this.isLastVisibleStep) {
        return 'Siguiente';
      }
      return 'Finalizar orden';
    },
  },
  watch: {
    dialog(val) {
      if (!val) this.clearPayment();
    },
    async invoiceRequired(val) {
      if (val === 'yes') {
        if (!this.paymentInvoiceMisc.cfdi_use?.length || !this.paymentInvoiceMisc.payment_type?.length || !this.paymentInvoiceMisc.payment_method?.length) {
          await this.preloadInvoiceMisc();
        }
        this.applyDefaultPaymentConditions();
        if (!this.invoiceClients?.length) {
          await this.preloadInvoiceClients();
        }
        this.billToSelectedClient = 'no';
        this.clearNominativeClientFields();
      }
    },
    async selectedClient() {
      if (this.invoiceRequired !== 'yes') return;
      this.billToSelectedClient = 'no';
      this.clearNominativeClientFields();
    },
    payments: {
      handler() {
        if (this.invoiceRequired === 'yes') {
          this.applyDefaultPaymentConditions();
        }
      },
      deep: true,
    },
    async realPaidCash() {
      if (this.invoiceRequired === 'yes') {
        this.applyDefaultPaymentConditions();
      }
    },
    async clientRegimeId() {
      if (this.clientRegimeId == null || this.clientRegimeId === '') {
        return;
      }
      this.$store.dispatch('fetchUsoCfdiItems', this.clientRegimeId || '');
    },
    paymentMethodTab(newVal, oldVal) {
      if (oldVal !== newVal) {
        this.clearPayment();
      }
      switch (newVal) {
        case 'cash':
          this.selectedPayment = 1;
          break;
        case 'card':
          this.selectedPayment = 1.5;
          break;
        case 'transfer':
          this.selectedPayment = 5;
          break;
        default:
          this.selectedPayment = 0;
      }
    },
    async billToSelectedClient(val) {
      if (this.invoiceRequired !== 'yes') return;
      const requestId = this.billToSelectedClientRequestId + 1;
      this.billToSelectedClientRequestId = requestId;

      if (val === 'no') {
        this.loadingFiscal = false;
        this.clearNominativeClientFields();
        return;
      }

      if (!this.selectedClient || !this.selectedClient.id) {
        this.loadingFiscal = false;
        return;
      }

      if (this.selectedClient && this.selectedClient.id) {
        await this.fillNominativeFromSelectedClient(requestId);
      }
    },
    pickupStoreId(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.syncAllItemPickupStores(newVal);
    },
  },
  methods: {
    formatCurrency(value) {
      return CurrencyFormatter.format(value);
    },
    toSafeNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? FPUtils.round2dec(n) : 0;
    },
    preventNegativeCurrencyInput(e) {
      if (e.key === '-' || e.key === 'Subtract' || e.keyCode === 189 || e.keyCode === 109) {
        e.preventDefault();
      }
    },
    allowOnlyNumericInput(e) {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter', 'Home', 'End'];
      const isControlKey = e.ctrlKey || e.metaKey || e.altKey;
      const isDigit = /^[0-9]$/.test(e.key);

      if (isDigit || allowedKeys.includes(e.key) || isControlKey) {
        return;
      }

      e.preventDefault();
    },
    sanitizeMoneyImport() {
      this.moneyImport = Math.max(0, this.toSafeNumber(this.moneyImport));
      this.getChange();
    },
    fillMoneyImportWithRemainingTotal() {
      this.moneyImport = Math.max(0, this.toSafeNumber(this.remainingTotal));
      this.getChange();
    },
    onlyDigits(value) {
      return String(value || '').replace(/\D/g, '');
    },
    trimMax(value, max = 100) {
      return String(value || '').trim().slice(0, max);
    },
    onClientDeliveryCheckboxChange(checked) {
      if (checked) {
        this.authorizedClientName = this.selectedClient.name || '';
        if (this.selectedClient.contactInfo) {
          this.authorizedClientContact = this.selectedClient.contactInfo.phoneNumber || '';
          this.authorizedClientEmail = this.selectedClient.contactInfo.email || '';
        }
      } else {
        this.authorizedClientName = '';
        this.authorizedClientContact = '';
        this.authorizedClientEmail = '';
      }
      this.deliveryValidationAttempted = false;
    },
    paymentMethodIdToName(id) {
      const mapping = {
        1: 'Efectivo',
        2: 'Tarjeta de débito',
        3: 'Tarjeta de crédito',
        5: 'Transferencia',
        6: 'Otro',
      };
      return mapping[id] || 'Desconocido';
    },
    removeCashPayment() {
      const cashPaid = this.toSafeNumber(this.realPaidCash);
      if (cashPaid <= 0) return;
      this.$store.dispatch('updateTotalOrderPaid', -cashPaid);
      this.realPaidCash = 0;
      this.moneyImport = 0;
      this.change = 0;
      this.transactionChange = 0;
    },
    removePayment(index) {
      if (index < 0 || index >= this.payments.length) return;
      const payment = this.payments[index];
      const amount = this.toSafeNumber(payment.totalImport);
      this.$store.dispatch('updateTotalOrderPaid', -amount);
      this.payments.splice(index, 1);
    },
    validateAmount(rawAmount, maxLen = 100) {
      const raw = String(rawAmount ?? '').trim();
      if (!raw) return 'Ingresa el importe a cobrar.';
      if (raw.length > maxLen) return `El importe no puede exceder ${maxLen} caracteres`;
      const amount = this.toSafeNumber(rawAmount);
      if (amount <= 0) return 'El importe a cobrar debe ser mayor a $0.00.';
      return true;
    },
    validateCurrentPaymentInput(paymentMethod, amount) {
      const amountValidation = this.validateAmount(amount);
      if (amountValidation !== true) return amountValidation;
      if (paymentMethod !== 1 && amount > this.toSafeNumber(this.remainingTotal)) {
        return 'El cobro con tarjeta/transferencia no puede superar el saldo restante de la orden';
      }
      if (paymentMethod === 2 || paymentMethod === 3) {
        const cardLastDigits = this.onlyDigits(this.cardLastNumbers);
        if (cardLastDigits.length !== 4) return 'Los últimos 4 dígitos de la tarjeta son obligatorios';
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
        if (!this.cardBrand) return 'Selecciona una marca de tarjeta';
        if (this.cardBrand === 'other' && !this.trimMax(this.customCardBrand, 100)) {
          return 'Especifica la marca de la tarjeta';
        }
      }
      if (paymentMethod === 4) {
        if (!this.trimMax(this.bankName, 100)) return 'Nombre del banco requerido';
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
      }
      if (paymentMethod === 5) {
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
      }
      return true;
    },
    validateAuthorizedClientName(value) {
      const authorizedName = this.trimMax(value, 100);
      if (!authorizedName) return 'Nombre del titular requerido';
      return true;
    },
    validateAuthorizedClientContact(value) {
      const contactDigits = this.onlyDigits(value);
      if (!contactDigits) return 'Telefono de contacto requerido';
      if (contactDigits.length < 10 || contactDigits.length > 15) {
        return 'Telefono de contacto invalido (10 a 15 digitos)';
      }
      return true;
    },
    validateAuthorizedClientEmail(value) {
      const email = this.trimMax(value, 100);
      if (!email) return 'Correo electronico requerido';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Correo electronico invalido';
      }
      return true;
    },
    validateNoInventoryForm() {
      if (!this.hasItemsWithNoInventory) return true;
      if (!this.pickupStoreId) return 'Selecciona la sucursal destino';

      const missingItemStore = (this.itemsWithNoInventory || []).some(
        (item, index) => !this.getItemPickupStoreId(item, index),
      );
      if (missingItemStore) {
        return 'Selecciona la sucursal destino para cada producto pendiente';
      }

      const nameValidation = this.validateAuthorizedClientName(this.authorizedClientName);
      if (nameValidation !== true) return nameValidation;

      const contactValidation = this.validateAuthorizedClientContact(this.authorizedClientContact);
      if (contactValidation !== true) return contactValidation;

      const emailValidation = this.validateAuthorizedClientEmail(this.authorizedClientEmail);
      if (emailValidation !== true) return emailValidation;

      return true;
    },
    getItemStoreKey(item, index) {
      const idPart = item?.id ?? item?.sku ?? item?.name ?? 'item';
      return `${idPart}-${index}`;
    },
    getItemPickupStoreId(item, index) {
      const key = this.getItemStoreKey(item, index);
      const selectedStoreId = this.itemPickupStoreByItemKey[key];
      if (selectedStoreId !== undefined && selectedStoreId !== null && selectedStoreId !== '') {
        return selectedStoreId;
      }
      return this.pickupStoreId || this.currentStoreId || null;
    },
    setItemPickupStoreId(item, index, storeId) {
      const key = this.getItemStoreKey(item, index);
      this.$set(this.itemPickupStoreByItemKey, key, storeId || null);
    },
    initializeItemPickupStores() {
      const fallbackStoreId = this.pickupStoreId || this.currentStoreId || null;
      const newStoresMap = { ...this.itemPickupStoreByItemKey };
      (this.itemsWithNoInventory || []).forEach((item, index) => {
        const key = this.getItemStoreKey(item, index);
        if (newStoresMap[key] === undefined) {
          newStoresMap[key] = fallbackStoreId;
        }
      });
      this.itemPickupStoreByItemKey = newStoresMap;
    },
    syncAllItemPickupStores(storeId) {
      const newStoresMap = { ...this.itemPickupStoreByItemKey };
      (this.itemsWithNoInventory || []).forEach((item, index) => {
        const key = this.getItemStoreKey(item, index);
        newStoresMap[key] = storeId || null;
      });
      this.itemPickupStoreByItemKey = newStoresMap; // Una sola actualización reactiva
    },
    buildDeliveryItemsPayload() {
      return (this.itemsWithNoInventory || []).map((item, index) => ({
        productId: item?.id ?? null,
        sku: item?.sku ?? null,
        backorder: Number(item?.backorder || 0),
        pickupStoreId: this.getItemPickupStoreId(item, index),
      }));
    },
    normalizePaymentCatalogItems(arr, codeKeys = ['code', 'value', 'clave', 'key'], labelKeys = ['label', 'name', 'descripcion', 'description']) {
      return (arr || []).map((x) => {
        const code = codeKeys.map((k) => x?.[k]).find((v) => v !== undefined && v !== null && v !== '');
        const label = labelKeys.map((k) => x?.[k]).find((v) => v !== undefined && v !== null && v !== '');
        return {
          ...x,
          code: String(code ?? '').trim(),
          label: String(label ?? '').trim(),
          display: `${String(code ?? '').trim()} - ${String(label ?? '').trim()}`,
        };
      });
    },
    hydratePaymentMetadataFromStore() {
      if (!this.paymentInvoiceMisc) {
        return;
      }

      this.formaPagos = this.normalizePaymentCatalogItems(this.paymentInvoiceMisc.payment_type);
      this.metodoPagos = this.normalizePaymentCatalogItems(this.paymentInvoiceMisc.payment_method);

      if (Array.isArray(this.paymentInvoiceClients)) {
        this.invoiceClients = this.paymentInvoiceClients;
      }
    },
    onClientRfcChange() {
      if (!this.isValidRfc(this.clientRfc)) {
        return;
      }
      this.$store.dispatch('fetchFiscalRegimes', this.clientRfc);
    },
    getMissingPaymentMetadata() {
      const missing = [];
      if (!this.paymentInvoiceMisc) missing.push('miscelánea de facturación');
      if (!Array.isArray(this.paymentInvoiceClients)) missing.push('clientes de facturación');
      if (!Array.isArray(this.paymentFiscalRegimes)) missing.push('regímenes fiscales');
      if (!Array.isArray(this.paymentUsoCfdiItems)) missing.push('usos CFDI');
      return missing;
    },
    async ensurePaymentMetadata(showWarning = true) {
      await this.$store.dispatch('preloadPaymentMetadata');
      this.hydratePaymentMetadataFromStore();

      const missing = this.getMissingPaymentMetadata();
      if (missing.length > 0) {
        if (showWarning) {
          Swal.fire({
            title: 'No se pudo habilitar la facturación',
            html: `No fue posible cargar:<br><br>${missing.map((item) => `• ${item}`).join('<br>')}<br><br>La venta puede continuar sin factura.`,
            icon: 'warning',
            confirmButtonText: 'Entendido',
          });
        }
        return false;
      }
      return true;
    },
    applyPickupStoreToProducts(products) {
      const noInventoryItems = this.itemsWithNoInventory || [];
      const buckets = new Map();
      const assignedProducts = products.map((originalProduct) => ({
        ...originalProduct,
        pickupStoreId: null,
      }));

      assignedProducts.forEach((product) => {
        const productKey = String(product?.productId ?? '');
        if (!buckets.has(productKey)) {
          buckets.set(productKey, []);
        }
        buckets.get(productKey).push(product);
      });

      noInventoryItems.forEach((item, index) => {
        const productKey = String(item?.id ?? '');
        const availableProducts = buckets.get(productKey) || [];
        const quantityToAssign = Math.max(0, Math.floor(Number(item?.backorder || 0)));
        const pickupStoreId = this.getItemPickupStoreId(item, index);

        for (let assigned = 0; assigned < quantityToAssign && availableProducts.length > 0; assigned += 1) {
          const product = availableProducts.shift();
          product.pickupStoreId = pickupStoreId || null;
        }
      });

      return assignedProducts;
    },
    buildPaymentsPayload() {
      const payload = this.payments.map((p) => ({
        totalImport: this.toSafeNumber(p.totalImport),
        paymentMethod: Number(p.paymentMethod),
        confirmationNumber: this.trimMax(p.confirmationNumber, 100) || null,
        cardLastDigits: this.onlyDigits(p.cardLastDigits).slice(0, 4) || null,
        cardBrand: this.trimMax(p.cardBrand, 100) || null,
        bankName: this.trimMax(p.bankName, 100) || null,
        change: this.toSafeNumber(p.change),
      })).filter((p) => p.totalImport > 0 && [1, 2, 3, 4, 5, 6].includes(p.paymentMethod));
      const cashAmount = this.toSafeNumber(this.realPaidCash);
      const hasCash = payload.some((x) => x.paymentMethod === 1);
      if (cashAmount > 0 && !hasCash) {
        payload.push({
          totalImport: cashAmount,
          paymentMethod: 1,
          confirmationNumber: null,
          cardLastDigits: null,
          cardBrand: null,
          bankName: null,
          change: this.toSafeNumber(this.cashChange),
        });
      }
      return payload;
    },
    getNetPaidAmount(paymentsPayload) {
      return FPUtils.round2dec(
        paymentsPayload.reduce((acc, p) => {
          if (p.paymentMethod === 1) return acc + (p.totalImport - this.toSafeNumber(p.change));
          return acc + p.totalImport;
        }, 0),
      );
    },
    resetPaymentState() {
      this.payments = [];
      this.realPaidCash = 0;
      this.$store.dispatch('clearTotalOrderPaid');
      this.dialog = false;
      this.clearPayment();
      this.resetInvoiceState();
    },
    satFormaPagoCodeFromPayments() {
      const totalsByMethod = {};
      this.payments.forEach((p) => {
        const pm = p.paymentMethod;
        const amt = Number(p.totalImport || 0);
        totalsByMethod[pm] = (totalsByMethod[pm] || 0) + amt;
      });
      if (this.realPaidCash && this.realPaidCash > 0) {
        totalsByMethod[1] = (totalsByMethod[1] || 0) + Number(this.realPaidCash);
      }
      const entries = Object.entries(totalsByMethod);
      if (entries.length === 0) return null;
      entries.sort((a, b) => Number(b[1]) - Number(a[1]));
      const pm = Number(entries[0][0]);
      if (pm === 1) return '01';
      if (pm === 4) return '02';
      if (pm === 5) return '03';
      if (pm === 2) return '28';
      if (pm === 3) return '04';
      return null;
    },
    satMetodoPagoCode() {
      return 'PUE';
    },
    async startPayment() {
      // Si todo está bien, abrir el diálogo de pago
      this.$store.dispatch('clearTotalOrderPaid');
      this.clearPayment();
      this.payments = [];
      this.realPaidCash = 0;
      this.moneyImport = null;
      this.resetInvoiceState();
      this.step = 1;
      this.syncTotalSteps();
      this.paymentMethodTab = 'cash';
      this.itemPickupStoreByItemKey = {};
      this.deliveryValidationAttempted = false;
      this.initializeItemPickupStores();

      // Always allow opening payment flow. Invoice requirements are validated
      // only when the user explicitly enables invoice.
      this.hydratePaymentMetadataFromStore();

      this.dialog = true;
    },
    async makeTransaction() {
      if (this.isProcessingPayment) return;
      this.isProcessingPayment = true;

      try {
        let paymentMethod = 0;
        const amount = this.toSafeNumber(this.moneyImport);
        if (this.selectedPayment === 1) {
          const validation = this.validateCurrentPaymentInput(1, amount);
          if (validation !== true) {
            Swal.fire('Error', validation, 'error');
            return;
          }
          this.realPaidCash = this.toSafeNumber(this.realPaidCash + amount);
          this.$store.dispatch('updateTotalOrderPaid', amount);
          this.clearPayment();
          return;
        }
        if (this.selectedPayment === 1.5) {
          if (!this.cardType || ![2, 3].includes(Number(this.cardType))) {
            Swal.fire('Error', 'Selecciona un tipo de tarjeta antes de continuar', 'error');
            return;
          }
          paymentMethod = Number(this.cardType);
        } else {
          paymentMethod = Number(this.selectedPayment);
        }
        const validation = this.validateCurrentPaymentInput(paymentMethod, amount);
        if (validation !== true) {
          Swal.fire('Error', validation, 'error');
          return;
        }
        this.payments.push({
          totalImport: amount,
          paymentMethod,
          confirmationNumber: this.trimMax(this.confirmationNumber, 100) || null,
          cardLastDigits: this.onlyDigits(this.cardLastNumbers).slice(0, 4) || null,
          cardBrand: this.cardBrand === 'other'
            ? this.trimMax(this.customCardBrand, 100)
            : this.trimMax(this.cardBrand, 100),
          bankName: this.trimMax(this.bankName, 100) || null,
        });
        this.$store.dispatch('updateTotalOrderPaid', amount);
        this.clearPayment();
      } finally {
        // Keep lock for a short window to avoid accidental double Enter submissions.
        setTimeout(() => {
          this.isProcessingPayment = false;
        }, 200);
      }
    },
    validateInvoiceForm() {
      if (this.invoiceRequired !== 'yes') return true;
      const rfc = String(this.clientRfc || '').trim().toUpperCase();
      const cp = String(this.clientCp || '').trim();
      if (!this.clientName?.trim()) return 'Ingresa la razón social';
      if (!(rfc.length === 12 || rfc.length === 13)) return 'El RFC debe tener 12 o 13 caracteres';
      if (!/^\d{5}$/.test(cp)) return 'CP inválido (5 dígitos)';
      if (!this.clientRegimeId) return 'Régimen fiscal requerido';
      if (!this.clientUsoCfdi) return 'Uso CFDI requerido';
      if (!this.clientPaymentMethod) return 'Forma de pago requerida';
      if (!this.clientPaymentType) return 'Método de pago requerido';
      return true;
    },
    async buildFacturaFromSale(purchaseOrderId) {
      const factura = await InvoicesService.getFacturaSale(purchaseOrderId, 1);
      factura.client = {
        code: String(this.clientRfc || '').trim().toUpperCase(),
        name: String(this.clientName || '').trim(),
        zip_code: String(this.clientCp || '').trim(),
        fiscal_regime_code: String(this.clientRegimeId || '').trim(),
      };
      factura.cfdi_use = {
        code: this.clientUsoCfdi?.code || this.clientUsoCfdi?.value,
        name: this.clientUsoCfdi?.name || this.clientUsoCfdi?.label || '',
      };
      factura.payment_type = {
        code: this.clientPaymentMethod?.code || this.clientPaymentMethod?.value,
        name: this.clientPaymentMethod?.name || this.clientPaymentMethod?.label || '',
      };
      factura.payment_method = {
        code: this.clientPaymentType?.code || this.clientPaymentType?.value,
        name: this.clientPaymentType?.name || this.clientPaymentType?.label || '',
      };
      return factura;
    },
    sanitizeConcepts(factura) {
      if (!Array.isArray(factura?.concepts)) return factura;
      const sanitizedConcepts = factura.concepts.map((c) => {
        const desc = String(c?.name || c?.description || '').trim();
        return {
          ...c,
          name: desc || 'PRODUCTO',
          description: desc || 'PRODUCTO',
        };
      });
      return {
        ...factura,
        concepts: sanitizedConcepts,
      };
    },
    formatSaleRegDate(dateValue) {
      const d = new Date(dateValue);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    async finishOrder() {
      const productsTotal = [];
      const products = [];
      this.block = 1;

      try {
        const toSafeNumber = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? FPUtils.round2dec(n) : 0;
        };
        const trimMax = (v, max = 100) => String(v || '').trim().slice(0, max);
        const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

        this.itemsList.forEach((x) => {
          const product = new PurchaseOrderProduct(x.id, x.baseCost, x.calculatedDiscount);
          product.isGift = x.isGift;
          products.push(product);
        });

        const productsWithPickupStore = this.applyPickupStoreToProducts(products);

        this.frontItemsList.forEach((item) => {
          productsTotal.push({ productId: item.id, total: item.quantity });
        });

        const paymentsPayload = this.payments
          .map((p) => ({
            totalImport: toSafeNumber(p.totalImport),
            paymentMethod: Number(p.paymentMethod),
            confirmationNumber: trimMax(p.confirmationNumber, 100) || null,
            cardLastDigits: onlyDigits(p.cardLastDigits).slice(0, 4) || null,
            cardBrand: trimMax(p.cardBrand, 100) || null,
            bankName: trimMax(p.bankName, 100) || null,
            change: toSafeNumber(p.change),
          }))
          .filter((p) => p.totalImport > 0 && [1, 2, 3, 4, 5, 6].includes(p.paymentMethod));

        const cashAmount = toSafeNumber(this.realPaidCash);
        const hasCashPayment = paymentsPayload.some((x) => x.paymentMethod === 1);
        if (cashAmount > 0 && !hasCashPayment) {
          paymentsPayload.push({
            totalImport: cashAmount,
            paymentMethod: 1,
            confirmationNumber: null,
            cardLastDigits: null,
            cardBrand: null,
            bankName: null,
            change: toSafeNumber(this.cashChange),
          });
        }

        if (paymentsPayload.length === 0) {
          Swal.fire('Atención', 'No hay pagos registrados. Añade un pago para poder finalizar la orden.', 'warning');
          this.block = 0;
          return;
        }

        const orderTotal = toSafeNumber(this.cartTotal.total);

        const nonCashPaid = FPUtils.round2dec(
          paymentsPayload.reduce((acc, p) => {
            if (p.paymentMethod === 1) return acc;
            return acc + toSafeNumber(p.totalImport);
          }, 0),
        );

        const cashPaid = FPUtils.round2dec(
          paymentsPayload.reduce((acc, p) => {
            if (p.paymentMethod !== 1) return acc;
            return acc + toSafeNumber(p.totalImport);
          }, 0),
        );

        const cashNeeded = FPUtils.round2dec(Math.max(0, orderTotal - nonCashPaid));
        const effectiveCashPaid = FPUtils.round2dec(Math.min(cashPaid, cashNeeded));

        const netPaidAmount = FPUtils.round2dec(nonCashPaid + effectiveCashPaid);

        if (FPUtils.round2dec(netPaidAmount - orderTotal) < 0) {
          Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Registra un pago adicional para completar la orden.', 'warning');
          this.block = 0;
          return;
        }

        if (this.remainingTotal > 0) {
          Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Registra un pago adicional para completar la orden.', 'warning');
          this.block = 0;
          return;
        }

        const noInventoryValidation = this.validateNoInventoryForm();
        if (noInventoryValidation !== true) {
          this.deliveryValidationAttempted = true;
          this.block = 0;
          return;
        }

        let delivery = null;
        if (this.hasItemsWithNoInventory) {
          delivery = {
            name: this.authorizedClientName,
            contact: this.authorizedClientContact,
            email: this.authorizedClientEmail,
          };
        }

        const invoiceValidation = this.validateInvoiceForm();
        if (invoiceValidation !== true) {
          Swal.fire('Error', invoiceValidation, 'error');
          this.block = 0;
          return;
        }

        const { total } = this.cartTotal;
        const employeeId = this.authUserData.user_claims.employee;
        let clientId;
        let clientDiscount = 0;

        if (this.selectedClient && this.selectedClient.id > 0) {
          clientId = this.selectedClient.id;
          clientDiscount = this.selectedClient.discount;
        }

        const resp = await PurchaseOrderService.finishPurchaseOrderV2(
          this.currentStoreId,
          employeeId,
          clientId,
          clientDiscount,
          total,
          paymentsPayload,
          productsWithPickupStore,
          productsTotal,
          delivery,
        );

        if (resp && resp.error) {
          Swal.fire({
            title: 'Error',
            html: `Hubo un error al registrar la venta:<br><br>${resp.message || 'Error desconocido'}`,
            icon: 'error',
          });
          this.block = 0;
          this.payments = [];
          this.realPaidCash = 0;
          this.$store.dispatch('clearTotalOrderPaid');
          this.dialog = false;
          this.clearPayment();
          this.resetInvoiceState();
          return;
        }

        const completedSaleId = resp?.data?.sale_order_id;
        const completedSaleFoil = resp?.data?.sale_order_foil;
        const completedSaleHasCollection = this.hasItemsWithNoInventory;

        if (this.savedQuotationPurchaseOrderId && this.savedQuotationPurchaseOrderId > 0) {
          const quotationOrderId = this.savedQuotationPurchaseOrderId;
          const response = await PurchaseOrderService.finishSavedPurchaseOrder(quotationOrderId);
          if (response && response.error) {
            Swal.fire('Error', 'Hubo un error al registrar los pagos', 'error');
            this.block = 0;
            return;
          }
          this.$store.dispatch('clearSavedQuotationItems');
        }

        // Impresion de Ticket
        await this.printTicket(completedSaleId);

        let invoiceResp = null;
        let invoiceError = null;
        if (this.invoiceRequired === 'yes') {
          try {
            let factura = await this.buildFacturaFromSale(completedSaleId);
            factura = this.sanitizeConcepts(factura);
            invoiceResp = await InvoicesService.postFacturalaSaleInvoice(factura);
            if (invoiceResp?.error || invoiceResp?.data?.error) {
              invoiceError = invoiceResp?.detail || invoiceResp?.data?.detail || 'No se pudo generar la factura';
            }
          } catch (e) {
            console.log(e);
            invoiceError = e?.detail || 'No se pudo generar la factura';
          }
        }

        await this.completeOrder(completedSaleId, completedSaleFoil, completedSaleHasCollection, invoiceResp?.data, invoiceError);
      } catch (e) {
        console.error(e);
        Swal.fire('Error', 'Ocurrió un error inesperado al finalizar la orden.', 'error');
      } finally {
        this.block = 0;
      }
    },
    async completeOrder(completedSaleId, completedSaleFoil, completedSaleHasCollection, invoiceResp, invoiceError) {
      this.payments = [];
      this.realPaidCash = 0;
      this.$store.dispatch('clearOrder');
      this.$store.dispatch('clearTotalOrderPaid');
      this.$store.dispatch('clearItemsWithNoInventory');
      this.clearPayment();
      this.authorizedClientName = '';
      this.authorizedClientContact = '';
      this.authorizedClientEmail = '';
      this.clientPickupAuthorized = false;

      this.resetInvoiceState();

      const { store } = this.authUserData.user_claims;
      const payload = await ProductService.getProductAutocompleteData('', 100, store, null);
      this.$store.dispatch('setProductsAutocomplete', payload);

      const payloadClient = await ClientService.getClientAutocompleteData();
      this.$store.dispatch('setClientsAutocomplete', payloadClient);

      this.completedSaleId = completedSaleId;
      this.completedSaleFoil = completedSaleFoil;
      this.completedSaleHasCollection = completedSaleHasCollection;
      this.completedSaleInvoiceError = invoiceError || '';
      this.completedSaleInvoiceResponse = invoiceResp || null;
      console.log(this.completedSaleInvoiceResponse);
      this.saleSuccessDialog = true;
      await this.$nextTick();
      this.dialog = false;
    },
    resetInvoiceState() {
      this.invoiceRequired = 'no';
      this.invoiceSwitchValue = false;
      this.billToSelectedClient = 'no';
      this.clearNominativeClientFields();
      this.clientPaymentMethod = null;
      this.clientPaymentType = null;
      this.lockFiscalRegime = false;
      this.clientRegime = null;
    },
    clearPayment() {
      this.moneyImport = 0;
      this.cardType = null;
      this.transactionChange = 0;
      this.cardLastNumbers = null;
      this.cardBrand = null;
      this.customCardBrand = '';
      this.confirmationNumber = null;
      this.bankName = null;
    },
    resetEverything() {
      this.resetPaymentState();
      this.clearNominativeClientFields();
      this.authorizedClientContact = '';
      this.authorizedClientEmail = '';
      this.authorizedClientName = '';
      this.clientPickupAuthorized = false;
      this.itemPickupStoreByItemKey = {};
    },
    async getChange() {
      const total = this.remainingTotal > 0 ? this.remainingTotal : 0;
      let change = FPUtils.round2dec(FPUtils.round2dec(this.moneyImport || 0) - total);
      if (change < 0) change = 0;
      this.transactionChange = CurrencyFormatter.format(change);
      this.change = change;
    },
    returnToOrder() {
      if (this.payments.length > 0 || this.realPaidCash) {
        const swal = Swal.fire({
          allowOutsideClick: false,
          title: 'Regresar a orden',
          confirmButtonText: 'Si',
          text: '¿Desea regresar a la orden? Se eliminarán los pagos realizados',
          icon: 'warning',
          showCancelButton: true,
          cancelButtonText: 'No',
        });
        swal.then((resp) => {
          if (resp.value) {
            this.clearPayment();
            this.payments = [];
            this.$store.dispatch('clearTotalOrderPaid');
            this.dialog = false;
            this.realPaidCash = 0;
            this.resetInvoiceState();
          }
        });
      } else {
        this.clearPayment();
        this.payments = [];
        this.$store.dispatch('clearTotalOrderPaid');
        this.dialog = false;
        this.realPaidCash = 0;
        this.resetInvoiceState();
      }
    },
    clearPayments() {
      const swal = Swal.fire({
        allowOutsideClick: false,
        title: 'Eliminar pagos',
        confirmButtonText: 'Si',
        text: '¿Seguro que desea eliminar los pagos realizados?',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'No',
      });
      swal.then((resp) => {
        if (resp.value) {
          this.clearPayment();
          this.payments = [];
          this.realPaidCash = 0;
          this.change = 0;
          this.transactionChange = 0;
          this.$store.dispatch('clearTotalOrderPaid');
          this.resetInvoiceState();
        }
      });
    },
    async printTicket(id) {
      const data = await PurchaseOrderService.getPurchaseOrderData(id);
      if (this.stompClient && this.stompClient.connected) {
        const ticketData = purchaseOrderToTicketData(data, this.ticketStruct);
        const payload = JSON.stringify(ticketData);
        this.stompClient.send('/exchange/orders/orders', payload, { 'content-type': 'application/json' });
      } else {
        // eslint-disable-next-line no-console
        console.error('no print client connected');
      }
    },
    async reprintCompletedSaleTicket() {
      if (!this.completedSaleId || this.reprintingSaleTicket) return;
      try {
        this.reprintingSaleTicket = true;
        await this.printTicket(this.completedSaleId);
      } catch {
        Swal.fire('Error', 'No se pudo reimprimir el ticket de venta.', 'error');
      } finally {
        this.reprintingSaleTicket = false;
      }
    },
    closeSaleSuccessDialog() {
      this.saleSuccessDialog = false;
      this.completedSaleId = null;
      this.completedSaleFoil = null;
      this.completedSaleHasCollection = false;
      this.completedSaleInvoiceError = '';
      this.completedSaleInvoiceResponse = null;
    },
    downloadCompletedSaleInvoice() {
      const item = this.completedSaleInvoiceResponse;
      this.invoicePdf(item);
      this.invoiceXml(item);
    },
    getInvoiceId(item) {
      return item?.id || null;
    },
    invoicePdf(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/pdf`, '_blank');
    },
    invoiceXml(item) {
      const invoiceId = this.getInvoiceId(item);
      if (!invoiceId) return;
      window.open(`${process.env.VUE_APP_BASEURL}/invoice/${invoiceId}/xml`, '_blank');
    },
    async preloadInvoiceMisc() {
      if (!this.paymentInvoiceMisc) {
        await this.$store.dispatch('preloadPaymentMetadata');
      }
      this.hydratePaymentMetadataFromStore();
      return !!this.paymentInvoiceMisc;
    },
    async preloadInvoiceClients() {
      if (!this.paymentInvoiceClients) {
        await this.$store.dispatch('preloadPaymentMetadata');
      }
      this.hydratePaymentMetadataFromStore();
      return Array.isArray(this.paymentInvoiceClients);
    },
    async loadClientFiscalData() {
      const clientId = this.selectedClient?.id;
      if (!clientId) return;
      try {
        this.loadingClientFiscal = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.loadingClientFiscal = false;
      }
    },
    findBySatCode(arr, satCode) {
      const norm = (v) => String(v ?? '').trim().toUpperCase();
      const target = norm(satCode);
      if (!target) return null;
      return (arr || []).find((x) => {
        const candidates = [x.value, x.code, x.clave, x.key, x.id, x.satCode, x.sat_code].map(norm);
        return candidates.includes(target);
      }) || null;
    },
    findBySatCodePadded(arr, satCode) {
      const norm = (v) => String(v ?? '').trim().toUpperCase();
      const target = norm(satCode);
      if (!target) return null;
      const alt = target.length === 1 ? `0${target}` : target;
      const alt2 = target.length === 2 && target.startsWith('0') ? String(parseInt(target, 10)) : target;
      return this.findBySatCode(arr, target)
        || this.findBySatCode(arr, alt)
        || this.findBySatCode(arr, alt2)
        || null;
    },
    isSatCutoffCase() {
      const baseDate = new Date();
      const isSaturday = baseDate.getDay() === 6;
      const monday = new Date(baseDate);
      monday.setDate(baseDate.getDate() + 2);
      const mondayIsNextMonth = monday.getMonth() !== baseDate.getMonth();
      const hasCardPayment = this.payments.some(
        (p) => Number(p.paymentMethod) === 2 || Number(p.paymentMethod) === 3,
      );
      return this.invoiceRequired === 'yes' && hasCardPayment && isSaturday && mondayIsNextMonth;
    },
    applyDefaultPaymentConditions() {
      const formaCode = this.satFormaPagoCodeFromPayments();
      const metodoCode = this.satMetodoPagoCode() || 'PUE';
      const isSatCutoffCase = this.isSatCutoffCase();
      if (isSatCutoffCase) {
        this.clientPaymentType = this.findBySatCodePadded(this.metodoPagos, '99');
        this.clientPaymentMethod = this.findBySatCode(this.formaPagos, 'PPD');
      } else {
        this.clientPaymentMethod = metodoCode
          ? this.findBySatCode(this.paymentInvoiceMisc.payment_type, metodoCode)
          : null;
        this.clientPaymentType = formaCode
          ? this.findBySatCodePadded(this.paymentInvoiceMisc.payment_method, formaCode)
          : null;
      }
    },
    isLatestBillToSelectedClientRequest(requestId) {
      return requestId === this.billToSelectedClientRequestId;
    },
    async fillNominativeFromSelectedClient(requestId = this.billToSelectedClientRequestId) {
      const clientId = this.selectedClient?.id;
      if (!clientId || !this.isLatestBillToSelectedClientRequest(requestId)) return;

      try {
        this.loadingFiscal = true;
        if (!this.invoiceClients?.length) await this.preloadInvoiceClients();
        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        if (!this.paymentInvoiceMisc.cfdi_use?.length || !this.paymentInvoiceMisc.payment_type?.length || !this.paymentInvoiceMisc.payment_method?.length) {
          await this.preloadInvoiceMisc();
          if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        }

        const item = this.invoiceClients.find((c) => {
          const anyId = c.id ?? c.clientId ?? c.value ?? c.customerId ?? c.personId;
          return Number(anyId) === Number(clientId);
        });
        if (!item || !this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.clientCp = item.zipCode ?? item.zip_code ?? item.cp ?? item.postalCode ?? null;
        this.clientName = (
          item.name ?? item.razonSocial ?? item.businessName ?? item.fullName ?? ''
        ).trim() || null;
        this.clientRfc = (
          item.rfc ?? item.taxId ?? item.tax_id ?? item.code ?? ''
        ).trim() || null;

        await this.loadClientFiscalData();
        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.applyClientFiscalRegime(item);
        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        if (!this.lockFiscalRegime && this.isValidRfc(this.clientRfc)) {
          if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        }

        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;

        this.applyDefaultPaymentConditions();

        if (!this.isLatestBillToSelectedClientRequest(requestId)) return;
        await this.$nextTick();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        if (this.isLatestBillToSelectedClientRequest(requestId)) {
          this.loadingFiscal = false;
        }
      }
    },
    async applyClientFiscalRegime(item) {
      const raw = item?.fiscalRegimeCode
        ?? item?.fiscal_regime_code
        ?? item?.fiscalRegimeId
        ?? item?.regimeId
        ?? null;
      if (raw === null) {
        return;
      }
      const rawStr = String(raw).trim();
      const catalog = await ClientService.fiscalRegimeCatalog('');
      const found = catalog.find((r) => String(r.value) === rawStr);
      if (found) {
        const extractedCode = found.label?.match(/^\[(\d+)\]/)?.[1] || null;
        this.clientRegimeId = extractedCode;
      }
    },
    clearNominativeClientFields() {
      this.clientName = '';
      this.clientRfc = '';
      this.clientCp = '';
      this.clientRegimeId = null;
      this.clientUsoCfdi = null;
      this.lockFiscalRegime = false;
      this.invoiceClientOptions = [];
      this.invoiceClientSelected = null;
      this.invoiceClientSearch = '';
    },
    clearInvoiceClientFields() {
      this.clientName = '';
      this.clientRfc = '';
      this.clientCp = '';
      this.clientRegimeId = null;
      this.clientUsoCfdi = null;
      this.clientPaymentMethod = null;
      this.clientPaymentType = null;
      this.invoiceClientSelected = null;
      this.invoiceClientSearch = '';
    },
    normalizeRegimeList(data = []) {
      return (data || []).map((x) => {
        const id = x?.id ?? x?.value ?? null;
        const rawLabel = String(x?.label ?? x?.name ?? '').trim();
        const match = rawLabel.match(/^\[(\d+)\]\s*(.*)$/);
        const code = String(x?.code ?? (match ? match[1] : '')).trim();
        const name = String(x?.name ?? (match ? match[2] : rawLabel)).trim();
        return {
          id: id != null ? String(id) : null,
          code,
          name,
          label: rawLabel || `${code} - ${name}`,
        };
      }).filter((x) => x.id || x.code);
    },
    isValidRfc(rfc) {
      const clean = String(rfc || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      const isValid = clean.length === 12 || clean.length === 13;
      return isValid;
    },
    nextStep() {
      if (this.step === 1 && this.remainingTotal > 0) {
        Swal.fire('Saldo pendiente', 'Falta saldo por cubrir. Verifica los montos ingresados antes de finalizar.', 'warning');
        return;
      }

      if (this.step === 2 && this.hasItemsWithNoInventory) {
        this.deliveryValidationAttempted = true;
        const noInventoryValidation = this.validateNoInventoryForm();
        if (noInventoryValidation !== true) {
          return;
        }
      }

      const currentIndex = this.visibleStepSequence.indexOf(this.step);
      if (currentIndex < 0) {
        const [firstStep] = this.visibleStepSequence;
        this.step = firstStep;
        return;
      }
      if (currentIndex < this.visibleStepSequence.length - 1) {
        this.step = this.visibleStepSequence[currentIndex + 1];
        return;
      }
      this.finishOrder();
    },
    previousStep() {
      if (this.step === 1) {
        this.dialog = false;
        this.resetEverything();
        return;
      }
      const currentIndex = this.visibleStepSequence.indexOf(this.step);
      if (currentIndex > 0) {
        this.step = this.visibleStepSequence[currentIndex - 1];
        return;
      }
      const [firstStep] = this.visibleStepSequence;
      this.step = firstStep;
    },
    syncTotalSteps() {
      this.totalSteps = this.renderedTotalSteps;
      if (!this.visibleStepSequence.includes(this.step)) {
        this.step = this.visibleStepSequence[this.visibleStepSequence.length - 1];
      }
    },
    async enableInvoiceForm(value) {
      if (this.isResolvingInvoiceToggle) {
        this.invoiceSwitchValue = this.invoiceRequired === 'yes';
        return;
      }

      if (!value) {
        this.invoiceRequired = 'no';
        this.invoiceSwitchValue = false;
        this.clearNominativeClientFields();
        this.clientPaymentMethod = null;
        this.clientPaymentType = null;
        this.lockFiscalRegime = false;
        this.clientRegime = null;
        this.syncTotalSteps();
        return;
      }

      this.isResolvingInvoiceToggle = true;
      try {
        const metadataReady = await this.ensurePaymentMetadata(true);
        this.invoiceRequired = metadataReady ? 'yes' : 'no';
        this.invoiceSwitchValue = metadataReady;

        if (!metadataReady) {
          this.clearNominativeClientFields();
          this.clientPaymentMethod = null;
          this.clientPaymentType = null;
          this.lockFiscalRegime = false;
          this.clientRegime = null;
        }
      } finally {
        this.isResolvingInvoiceToggle = false;
        this.syncTotalSteps();
      }
    },
    async searchInvoiceClient(query) {
      if (this.pendingInvoiceClientRequest) {
        this.pendingInvoiceClientRequest.cancel();
        this.pendingInvoiceClientRequest = null;
      }

      this.invoiceClientSearchTimeout = setTimeout(async () => {
        await this.performInvoiceClientSearch(query);
      }, this.invoiceClientSearchDebounceDelay);
      this.pendingInvoiceClientRequest = {
        cancel: () => clearTimeout(this.invoiceClientSearchTimeout),
      };
    },
    async performInvoiceClientSearch(query) {
      this.invoiceClientSearch = (query || '').trim();
      if (this.invoiceClientSearch.length < this.invoiceClientSearchMinLength) {
        this.invoiceClientOptions = [];
        return;
      }
      try {
        this.loadingInvoiceClients = true;
        this.invoiceClientOptions = await InvoicesService.getInvoiceClientsAutocomplete(
          this.invoiceClientSearch,
          20,
        );
      } catch (e) {
        console.error(e);
        this.invoiceClientOptions = [];
      } finally {
        this.loadingInvoiceClients = false;
      }
    },
    async onInvoiceClientChange(selected) {
      if (!selected) return;
      this.clientName = selected.name || '';
      this.clientRfc = (selected.rfc || '').toUpperCase();
      this.clientCp = selected.zipCode || '';
      this.clientRegimeId = this.fiscalRegimes.find((regime) => regime.value === selected.fiscalRegimeId).value || null;
    },
    onlyNumberKeypress(e) {
      const char = String.fromCharCode(e.which || e.key || e.keyCode);
      if (!/[0-9]/.test(char)) e.preventDefault();
    },
    onCpPaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData('text') || '';
      this.clientCp = text.replace(/\D/g, '').slice(0, 5);
    },
  },
  data: () => ({
    currentStoreId: null,
    dialog: false,
    step: 1,
    totalSteps: 1,
    deliveryValidationAttempted: false,
    moneyImport: 0,
    payments: [],
    change: 0,
    realPaidCash: 0,
    block: 0,
    isProcessingPayment: false,
    selectedPayment: 1,
    cardType: 0,
    cardBrand: null,
    customCardBrand: '',
    cardLastNumbers: null,
    confirmationNumber: null,
    bankName: null,
    transactionChange: 0,
    paymentMethodTab: 'cash',
    saleSuccessDialog: false,
    completedSaleId: null,
    completedSaleFoil: null,
    completedSaleHasCollection: false,
    completedSaleInvoiceError: '',
    completedSaleInvoiceResponse: null,
    reprintingSaleTicket: false,
    paymentCardBrands: [
      {
        src: visa,
        value: 'visa',
      },
      {
        src: masterCard,
        value: 'mastercard',
      },
      {
        src: americanEx,
        value: 'american-express',
      },
    ],
    invoiceRequired: 'no',
    invoiceSwitchValue: false,
    showNominativeModal: false,
    loadingInvoiceClients: false,
    pendingInvoiceClientRequest: null,
    invoiceClientSearchTimeout: null,
    invoiceClientSearchDebounceDelay: 500,
    invoiceClientSearchMinLength: 2,
    invoiceClients: [],
    invoiceClientOptions: [],
    invoiceClientSearch: '',
    invoiceClientSelected: null,
    clientName: '',
    clientRfc: '',
    clientCp: '',
    clientRegime: null,
    clientUsoCfdi: null,
    clientPaymentMethod: null,
    clientPaymentType: null,
    clientRegimeId: null,
    fiscalRegimeSearch: '',
    loadingFiscalRegimes: false,
    lockFiscalRegime: false,
    loadingUsoCfdi: false,
    loadingFiscal: false,
    billToSelectedClientRequestId: 0,
    billToSelectedClient: 'no',
    isResolvingInvoiceToggle: false,
    pickupStoreId: null,
    itemPickupStoreByItemKey: {},
    clientPickupAuthorized: false,
    authorizedClientName: null,
    authorizedClientContact: null,
    authorizedClientEmail: null,
    rfcRules: [
      (v) => !v || v.length >= 12 || 'RFC mínimo 12 caracteres',
      (v) => !v || v.length <= 13 || 'RFC máximo 13 caracteres',
      (v) => !v || /^[A-Z0-9]+$/.test(v) || 'RFC solo letras y números',
    ],
    cpRules: [
      (v) => !v || /^\d{5}$/.test(v) || 'CP debe tener 5 dígitos',
    ],
    swalText: '¿Desea cerrar la orden y proceder al pago?',
  }),
  mounted() {
    this.syncTotalSteps();
    const { store } = this.authUserData.user_claims;
    this.currentStoreId = store;
    this.pickupStoreId = this.currentStoreId;
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center">
      <div class="d-flex flex-column align-center">
        <div class="text-center text-h6 text-lg-h5">Total a Pagar</div>
        <h1 class="">{{ cartTotal.FTotal }}</h1>
      </div>
      <div v-if="canViewPos" class="d-flex">
        <v-btn
          v-if="itemsList.length > 0"
          @click="startPayment"
          block
          color="secondary"
          x-large>
          PAGAR
        </v-btn>
      </div>
    </div>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="1000px"
      transition="dialog-bottom-transition"
    >
      <v-card class="payment-dialog-card">
        <div class="custom-dialog-header">
          <div class="d-flex vertical-align-center">
            <v-icon color="primary">mdi-cash-register</v-icon>
            <span class="text-h5 font-weight-bold">Registrar Formas de Pago</span>
          </div>
          <div>
            <span class="text-body-1">Paso {{ renderedStep }} de {{ renderedTotalSteps }}</span>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="pa-4">
          <v-row>
            <v-col cols="12" sm="4">
              <div class="d-flex flex-column">
                <div style="border: 1px solid #e0e0e0; padding: 16px;">
                  <div class="text-center text-h6 text-lg-h5">
                    <span class="text-caption">TOTAL A PAGAR</span>
                    <h1 class="text-h4 font-weight-bold">{{ cartTotal.FTotal }}</h1>
                  </div>
                </div>
                <div class="d-flex flex-column my-4">
                  <div class="d-flex justify-space-between">
                    <span class="text-body-1">Total Pagado:</span>
                    <span><strong>{{ FTotalOrderPaid }}</strong></span>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span class="text-body-1">Total Restante:</span>
                    <span><strong>{{ FRemainingTotal }}</strong></span>
                  </div>
                </div>
                <v-divider></v-divider>
                <div class="d-flex justify-space-between my-6 mx-2">
                  <span class="text-body-1">Cambio del Cliente:</span>
                  <span><strong>{{ FChange }}</strong></span>
                </div>
                <div v-if="(realPaidCash > 0 || payments.length > 0) && step == 1">
                  <span class="text-body-1 font-weight-bold">ABONOS REGISTRADOS:</span>
                  <div>
                    <v-chip
                      v-if="realPaidCash > 0"
                      class="ma-1"
                      color="primary"
                      outlined
                    >
                      <span class="mr-2">Efectivo:</span>
                      <span class="font-weight-bold">{{ FRealPaidCash }}</span>
                      <v-btn small icon @click="removeCashPayment" class="ml-2">
                        <v-icon small>mdi-close-circle</v-icon>
                      </v-btn>
                    </v-chip>
                    <v-chip
                      v-for="(p, idx) in payments"
                      :key="idx"
                      class="ma-1"
                      color="primary"
                      outlined
                    >
                      <span class="mr-2">{{ paymentMethodIdToName(p.paymentMethod) }}:</span>
                      <span class="font-weight-bold">{{ formatCurrency(p.totalImport) }}</span>
                      <v-btn small icon @click="removePayment(idx)" class="ml-2">
                        <v-icon small>mdi-close-circle</v-icon>
                      </v-btn>
                    </v-chip>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="8">
              <v-window v-model="step">
                <v-window-item :value="1">
                  <div class="d-flex flex-column">
                    <span class="text-body-1 font-weight-bold">Selecciona un método de pago alterno o combinado:</span>
                    <v-tabs
                      v-model="paymentMethodTab"
                      grow
                      class="mb-2"
                    >
                      <v-tab href="#cash">Efectivo</v-tab>
                      <v-tab href="#card">Tarjeta</v-tab>
                      <v-tab href="#transfer">Transferencia</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="paymentMethodTab">
                      <v-tab-item :value="'cash'">
                        <div class="d-flex flex-column py-2">
                          <v-currency-field
                            v-model="moneyImport"
                            outlined
                            prepend-icon="mdi-cash"
                            @keydown.native="preventNegativeCurrencyInput"
                            @keypress.enter="makeTransaction"
                            @input="sanitizeMoneyImport"
                            color="secondary"
                            label="Importe efectivo"
                          ></v-currency-field>
                        </div>
                      </v-tab-item>
                      <v-tab-item :value="'card'">
                        <div class="d-flex flex-column py-2">
                          <v-radio-group
                            v-model="cardBrand"
                            row
                            class="pa-0 ma-0 radio-group-space-between"
                          >
                            <v-radio
                              color="secondary"
                              v-for="(provider, idx) in paymentCardBrands"
                              :key="idx"
                              :value="provider.value"
                            >
                              <template #label>
                                <v-img :src="provider.src" max-width="80"/>
                              </template>
                            </v-radio>
                            <v-radio color="secondary" :value="'other'">
                              <template #label>
                                <span class="mr-2">Otra:</span>
                              </template>
                            </v-radio>
                          </v-radio-group>
                          <v-text-field
                            v-if="cardBrand === 'other'"
                            v-model="customCardBrand"
                            dense
                            hide-details
                            solo
                            maxlength="50"
                            counter="50"
                            placeholder="Especificar"
                            class="mb-4"
                          />
                          <v-radio-group
                            v-model="cardType"
                            row
                            class="pa-0 ma-0 radio-group-space-between"
                          >
                            <v-radio color="secondary" label="Tarjeta de débito" :value="2" />
                            <v-radio color="secondary" label="Tarjeta de crédito" :value="3" />
                          </v-radio-group>
                          <v-currency-field
                            v-model="moneyImport"
                            outlined
                            prepend-icon="mdi-credit-card"
                            @keydown.native="preventNegativeCurrencyInput"
                            @keypress.enter="makeTransaction"
                            @input="sanitizeMoneyImport"
                            append-icon="mdi-arrow-down-bold-circle-outline"
                            @click:append="fillMoneyImportWithRemainingTotal"
                            color="secondary"
                            label="Importe tarjeta"
                          ></v-currency-field>
                          <v-text-field
                            v-model="cardLastNumbers"
                            type="text"
                            inputmode="numeric"
                            maxlength="4"
                            counter="4"
                            @input="cardLastNumbers = onlyDigits($event || '').slice(0, 4)"
                            @keydown="allowOnlyNumericInput"
                            @keypress.enter="makeTransaction"
                            outlined
                            prepend-icon="mdi-numeric"
                            class="pb-4"
                            color="secondary"
                            label="Últimos 4 dígitos de la tarjeta"
                          />
                          <v-text-field
                            v-model="confirmationNumber"
                            type="text"
                            inputmode="numeric"
                            maxlength="50"
                            counter="50"
                            @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
                            @keydown="allowOnlyNumericInput"
                            @keypress.enter="makeTransaction"
                            outlined
                            prepend-icon="mdi-shield-check-outline"
                            color="secondary"
                            label="Número de confirmación"
                          />
                        </div>
                      </v-tab-item>
                      <v-tab-item :value="'check'">
                        <div class="d-flex flex-column py-2">
                          <v-currency-field v-model="moneyImport"
                            @keydown.native="preventNegativeCurrencyInput"
                            @keypress.enter="makeTransaction"
                            outlined prepend-icon="mdi-checkbook"
                            class="pb-4"
                            @input="sanitizeMoneyImport"
                            append-icon="mdi-arrow-down-bold-circle-outline"
                            @click:append="fillMoneyImportWithRemainingTotal"
                            color="secondary" label="Importe cheque">
                          </v-currency-field>
                          <v-text-field
                            v-model="bankName"
                            maxlength="50"
                            counter="50"
                            @keypress.enter="makeTransaction"
                            outlined
                            prepend-icon="mdi-bank"
                            class="pb-4"
                            color="secondary"
                            label="Nombre del banco"
                          />
                          <v-text-field
                            v-model="confirmationNumber"
                            type="text"
                            inputmode="numeric"
                            maxlength="50"
                            counter="50"
                            @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
                            @keydown="allowOnlyNumericInput"
                            @keypress.enter="makeTransaction"
                            outlined
                            prepend-icon="mdi-shield-check-outline"
                            color="secondary"
                            label="Número de confirmación"
                          />
                        </div>
                      </v-tab-item>
                      <v-tab-item :value="'transfer'">
                        <div class="d-flex flex-column py-2">
                          <v-currency-field
                            v-model="moneyImport"
                            @keydown.native="preventNegativeCurrencyInput"
                            @keypress.enter="makeTransaction"
                            class="pb-4"
                            outlined prepend-icon="mdi-transfer-right"
                            @input="sanitizeMoneyImport"
                            append-icon="mdi-arrow-down-bold-circle-outline"
                            @click:append="fillMoneyImportWithRemainingTotal"
                            color="secondary" label="Importe transferencia">
                          </v-currency-field>
                          <v-text-field
                            v-model="confirmationNumber"
                            type="text"
                            inputmode="numeric"
                            maxlength="50"
                            counter="50"
                            @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
                            @keydown="allowOnlyNumericInput"
                            @keypress.enter="makeTransaction"
                            outlined
                            prepend-icon="mdi-shield-check-outline"
                            color="secondary"
                            label="Número de confirmación"
                          />
                        </div>
                      </v-tab-item>
                    </v-tabs-items>
                    <v-btn
                      color="primary"
                      class="my-4"
                      @click="makeTransaction"
                      :disabled="disableMakeTransactionButton"
                    >Registrar Pago</v-btn>
                  </div>
                </v-window-item>
                <v-window-item :value="2">
                  <div class="d-flex justify-space-around">
                    <div class="pr-2">
                      <div class="d-flex">
                        <v-icon class="mr-1" color="primary">mdi-truck</v-icon>
                        <span class="text-body-2 font-weight-bold">Metodo de Entrega</span>
                      </div>
                      <span class="text-body-2">Canal de distribución y destino de resguardo</span>
                      <v-text-field
                        value="Recoger en esta sucursal"
                        label="Destino de Entrega"
                        outlined
                        hide-details
                        readonly
                        disabled
                        class="mb-2 mt-2"
                      />
                      <v-select
                        v-model="pickupStoreId"
                        :items="stores"
                        item-text="name"
                        item-value="id"
                        label="Sucursal Destino"
                        outlined
                        hide-details
                        disabled
                      ></v-select>
                    </div>
                    <div class="d-flex flex-column">
                      <div class="d-flex">
                        <v-icon class="mr-1" color="primary">mdi-account-plus</v-icon>
                        <span class="text-body-2 font-weight-bold">Responsable del Retiro</span>
                      </div>
                      <span class="text-body-2">Persona fisica autorizada para firmar la entrega</span>
                      <v-checkbox
                        v-model="clientPickupAuthorized"
                        @change="onClientDeliveryCheckboxChange"
                        label="El cliente titular de la cuenta recoge"
                        :disabled="selectedClient === null"
                      ></v-checkbox>
                      <v-text-field
                        v-model="authorizedClientName"
                        label="Nombre del titular *"
                        outlined
                        class="mb-2"
                        maxlength="100"
                        counter="100"
                        :rules="[validateAuthorizedClientName]"
                        :error-messages="deliveryValidationAttempted && validateAuthorizedClientName(authorizedClientName) !== true ? [validateAuthorizedClientName(authorizedClientName)] : []"
                        hint="Obligatorio."
                        persistent-hint
                        prepend-inner-icon="mdi-account"
                      ></v-text-field>
                      <v-text-field
                        v-model="authorizedClientContact"
                        label="Telefono de contacto *"
                        outlined
                        class="mb-2"
                        type="text"
                        inputmode="numeric"
                        maxlength="10"
                        counter="10"
                        @input="authorizedClientContact = onlyDigits($event || '').slice(0, 10)"
                        @keydown="allowOnlyNumericInput"
                        :rules="[validateAuthorizedClientContact]"
                        :error-messages="deliveryValidationAttempted && validateAuthorizedClientContact(authorizedClientContact) !== true ? [validateAuthorizedClientContact(authorizedClientContact)] : []"
                        hint="Obligatorio. 10 digitos."
                        persistent-hint
                        prepend-inner-icon="mdi-phone"
                      ></v-text-field>
                      <v-text-field
                        v-model="authorizedClientEmail"
                        label="Correo electronico *"
                        outlined
                        class="mb-2"
                        type="email"
                        maxlength="100"
                        counter="100"
                        :rules="[validateAuthorizedClientEmail]"
                        :error-messages="deliveryValidationAttempted && validateAuthorizedClientEmail(authorizedClientEmail) !== true ? [validateAuthorizedClientEmail(authorizedClientEmail)] : []"
                        hint="Obligatorio. Ejemplo: nombre@dominio.com"
                        persistent-hint
                        prepend-inner-icon="mdi-email"
                      ></v-text-field>
                    </div>
                  </div>
                </v-window-item>
                <v-window-item v-if="invoiceRequired === 'yes'" :value="3">
                  <div class="mt-2">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="text-subtitle-2">
                        Facturar a nombre del cliente
                      </div>
                      <v-btn
                        small
                        outlined
                        color="primary"
                        class="px-2"
                        @click="clearInvoiceClientFields"
                      >
                        Limpiar campos
                      </v-btn>
                    </div>
                    <v-radio-group
                      v-model="billToSelectedClient"
                      row
                      class="pa-0 ma-0"
                      :disabled="selectedClient === null"
                    >
                      <v-radio color="secondary" label="Sí" value="yes" />
                      <v-radio color="secondary" label="No" value="no" />
                    </v-radio-group>
                    <v-autocomplete
                      v-model="invoiceClientSelected"
                      :items="invoiceClientOptions"
                      :search-input.sync="invoiceClientSearch"
                      :loading="loadingInvoiceClients"
                      item-text="label"
                      return-object
                      no-filter
                      outlined
                      dense
                      clearable
                      hide-no-data
                      label="Buscar por Razón Social / RFC"
                      @update:search-input="searchInvoiceClient"
                      @change="onInvoiceClientChange"
                    />
                    <div class="text-subtitle-1">
                      Datos del cliente
                    </div>
                    <v-row>
                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model="clientName"
                          outlined
                          dense
                          label="Razón Social"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="clientRfc"
                          outlined
                          dense
                          label="RFC"
                          maxlength="13"
                          :rules="rfcRules"
                          @input="clientRfc = (clientRfc || '').toUpperCase()"
                          clearable
                          @change="onClientRfcChange"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="clientCp"
                          outlined
                          dense
                          label="Código Postal"
                          maxlength="5"
                          inputmode="numeric"
                          @keypress="onlyNumberKeypress"
                          @paste.prevent="onCpPaste"
                          :rules="cpRules"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="clientRegimeId"
                          :items="fiscalRegimes"
                          :loading="loadingFiscalRegimes"
                          item-text="label"
                          item-value="value"
                          outlined
                          dense
                          clearable
                          label="Régimen Fiscal"
                          hide-no-data
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-autocomplete
                          v-model="clientUsoCfdi"
                          :items="usoCfdiItems"
                          :loading="loadingUsoCfdi"
                          item-text="display"
                          return-object
                          outlined
                          dense
                          label="Uso CFDI"
                        />
                      </v-col>
                    </v-row>
                    <div class="text-subtitle-1">
                      Condiciones de pago
                    </div>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                        v-model="clientPaymentType"
                        :items="metodoPagos"
                        item-text="display"
                        return-object
                        outlined
                        dense
                        label="Forma de pago"
                        :disabled="lockSatPaymentFields"
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-autocomplete
                        v-model="clientPaymentMethod"
                        :items="formaPagos"
                        item-text="display"
                        return-object
                        outlined
                        dense
                        label="Metodo de pago"
                        :disabled="lockSatPaymentFields"
                        />
                      </v-col>
                    </v-row>
                    <v-alert
                      v-if="lockSatPaymentFields"
                      type="info"
                      dense
                      outlined
                    >
                      Por corte de fin de mes en sábado con pago en tarjeta, se asigna
                      automáticamente:
                      Forma de pago 99 (Por definir) y Método de pago PPD.
                    </v-alert>
                  </div>
                </v-window-item>
              </v-window>
            </v-col>
          </v-row>
        </div>
        <div class="pa-2" v-if="step === 2">
          <div class="d-flex align-center ml-2">
            <v-icon small class="mr-1">mdi-information-outline</v-icon>
            <span class="text-body-2 font-weight-medium">Articulos para entrega posterior</span>
          </div>
          <v-simple-table>
            <thead>
              <tr>
                <th class="text-left">Producto</th>
                <th class="text-left">Marca</th>
                <th class="text-left">Modelo</th>
                <th class="text-left">SKU / IDENTIFICADOR</th>
                <th class="text-center">Piezas a Entregar</th>
                <th class="text-left pickup-store-col">Sucursal de Entrega</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in itemsWithNoInventory" :key="index">
                <td>{{ item.name }}</td>
                <td>{{ item.brand ? item.brand.name : '' }}</td>
                <td>{{ item.model }}</td>
                <td>{{ item.sku }}</td>
                <td class="text-center">{{ item.backorder }}</td>
                <td class="pickup-store-col">
                  <v-select
                    :value="getItemPickupStoreId(item, index)"
                    :items="stores"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="pickup-store-field"
                    @change="setItemPickupStoreId(item, index, $event)"
                    disabled
                  ></v-select>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
        <div class="py-2 px-2 d-flex justify-space-between align-center">
          <v-btn @click="previousStep">
            <span v-if="step === 1">Cancelar</span>
            <span v-else>Regresar</span>
          </v-btn>
          <div class="d-flex align-center">
            <v-switch
              v-model="invoiceSwitchValue"
              @change="enableInvoiceForm"
               v-if="remainingTotal <= 0"
               label="Requiere Facturar"
               class="mr-4"
              :loading="isResolvingInvoiceToggle"
              :disabled="isResolvingInvoiceToggle"
               dense
               hide-details
            >
              <v-icon class="mr-2">mdi-checkbox-blank-outline</v-icon>
            </v-switch>
            <v-btn
              color="primary"
              @click="nextStep"
              :disabled="block === 1"
            >{{ finishOrderBtnText }}</v-btn>
          </div>
        </div>
        <v-overlay
          absolute
          :value="block === 1"
          color="white"
          opacity="0.85"
        >
          <div class="d-flex flex-column align-center justify-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="56"
              width="5"
            />
            <span class="mt-4 text-subtitle-1 font-weight-medium">
              Finalizando orden...
            </span>
          </div>
        </v-overlay>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="saleSuccessDialog"
      persistent
      max-width="640px"
    >
      <sale-success-actions
        :sale-id="completedSaleFoil"
        :show-collection-ticket="completedSaleHasCollection"
        :printing-ticket="reprintingSaleTicket"
        :invoice-response="completedSaleInvoiceResponse"
        :invoice-error-message="completedSaleInvoiceError"
        @reprint-ticket="reprintCompletedSaleTicket"
        @download-invoice="downloadCompletedSaleInvoice"
        @close="closeSaleSuccessDialog"
      />
    </v-dialog>
  </div>
</template>

<style lang="less">
.custom-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}
.radio-group-space-around div[role="radiogroup"] {
  display: flex !important;
  width: 100% !important;
  justify-content: space-around !important;
}
.radio-group-space-between div[role="radiogroup"] {
  display: flex !important;
  width: 100% !important;
  justify-content: space-between !important;
}
.client-box {
  border-top: 1px solid rgba(255,255,255,0.12);
  padding-top: 12px;
}
.v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

.pickup-store-col {
  width: 180px;
  min-width: 180px;
}

.pickup-store-field {
  max-width: 170px;
}
</style>
